import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ApiRequest } from '../common/services/api-request';

export interface LodgeImage {
  listing_image_id: string;
  title?: string;
  credit?: string;
  thumb_url?: string;
  main_src?: string;
}

export interface LodgeDetails {
  listing_id: string;
  name: string;
  banner_title?: string;
  banner_intro?: string;
  title?: string;
  short_desc?: string;
  brief_info?: string;
  content?: string;
  logo_src?: string;
  featured_image_src?: string;
  tags?: string;
  counters?: string;
  booking_steps?: string;
  availability_data?: string;
  full_slug?: string;
  status_nm?: string;
  updated_time?: string;
  video_url?: string;
  images?: LodgeImage[];
  info_email?: string;
  info_map_add?: string;
}

export interface LodgeOffer {
  listing_offer_id: string;
  title: string;
  url_slug?: string;
  details?: string;
  valid_from?: string;
  valid_to?: string;
  exclusive_offer?: string;
  image_src?: string;
  document_src?: string;
}

@Injectable({ providedIn: 'root' })
export class LodgeStateService {
  private lodgeDetailsSubject = new BehaviorSubject<LodgeDetails | null>(null);
  private offersSubject = new BehaviorSubject<LodgeOffer[]>([]);
  private galleryImagesSubject = new BehaviorSubject<LodgeImage[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private lodgeDetailsLoaded = false;
  private offersLoaded = false;
private lodgeSubject = new BehaviorSubject<LodgeDetails | null>(null);
lodge$ = this.lodgeSubject.asObservable();
  lodgeDetails$ = this.lodgeDetailsSubject.asObservable();
  offers$ = this.offersSubject.asObservable();
  galleryImages$ = this.galleryImagesSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  constructor(private apiRequest: ApiRequest) {}

  setLodgeDetails(data: LodgeDetails | null) {
    this.lodgeDetailsSubject.next(data);
    this.setGalleryImages(data?.images || []);
    this.lodgeDetailsLoaded = true;
  }

  setOffers(data: LodgeOffer[]) {
    this.offersSubject.next(data || []);
    this.offersLoaded = true;
  }

  setGalleryImages(data: LodgeImage[]) {
    this.galleryImagesSubject.next(data || []);
  }

  async refreshLodgeDetails(force = false) {
    if (this.lodgeDetailsLoaded && !force) {
      return this.lodgeDetailsSubject.value;
      
    }

    const response = await firstValueFrom(this.apiRequest.getLodgeDetails());
    this.lodgeSubject.next(response.data);
    const details = this.normalizeDetails(response);
    this.setLodgeDetails(details);
    return details;
  }

  async refreshOffers(force = false) {
    if (this.offersLoaded && !force) {
      return this.offersSubject.value;
    }

    const response = await firstValueFrom(this.apiRequest.getLodgeOffers());
    const offers = this.normalizeOffers(response);
    this.setOffers(offers);
    return offers;
  }

  async refreshAll(force = false) {
    this.loadingSubject.next(true);

    try {
      const [details, offers] = await Promise.all([
        this.refreshLodgeDetails(force),
        this.refreshOffers(force),
      ]);

      return { details, offers };
    } finally {
      this.loadingSubject.next(false);
    }
  }

  updateLocalLodgeDetails(updatedData: Partial<LodgeDetails>) {
    const current = this.lodgeDetailsSubject.value;
    const next = {
      ...(current || {}),
      ...updatedData,
    } as LodgeDetails;

    this.setLodgeDetails(next);
  }

  private normalizeDetails(response: any): LodgeDetails {
    return response?.data?.data ?? response?.data ?? response;
  }

  private normalizeOffers(response: any): LodgeOffer[] {
    const offers = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(offers) ? offers : [];
  }
}
